import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { PayPeriod } from '@app/interfaces';
import { Schedule } from '@app/models/payroll/schedule.model';
import { FilterPayrollReportForm } from '@app/modules/forms/reporting/components';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { ReportBook } from '@app/modules/reporting/classes/report-book';
import { ReportOptions } from '@app/modules/reporting/interfaces/report-options.interface';
import { ReportSheet } from '@app/modules/reporting/interfaces/report-sheet.interface';
import { FilterValues } from '@app/modules/table/interfaces/filter-values.interface';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { PayrollReport } from '@reporting/interfaces/report.interface';
import { Benefit, JournalEntry, PayrollRegister, Remittance, Reports, YTD } from '@reporting/reports';
import { PayrollReportingService } from '@reporting/services';
import { format, isWithinRange, parse } from 'date-fns';
import moment from 'moment';
import stickybits from 'stickybits';

@Component({
    templateUrl: './standard-payroll-report.view.html',
    styleUrls: ['./standard-payroll-report.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardPayrollReportView implements OnInit {
    @ViewChild('filterForm', { static: true }) filterForm!: FilterPayrollReportForm;

    startAt: Date = new Date();
    endAt: Date = new Date();
    payPeriodsStartAt: Date | null = null;
    payPeriodsEndAt: Date | null = null;
    firstChequeDate: Date | null = null;
    lastChequeDate: Date | null = null;
    isSingleDayRange = false;

    isLoading = true;
    filters = {};
    filterValues = {};
    activeFilters: any = {
        DateRange: null,
        Filters: {},
    };
    includePending = true;

    report: PayrollReport | null | undefined = null;
    reportBook: ReportBook | null = null;
    activeSheet: ReportSheet | null = null;
    activeSheetIndex = 0;
    numberOfStickyColumns = 0;
    hasMultipleExportOptions = false;
    urlPassedFilters: FilterValues | null = null;
    schedule: Schedule | null = null;
    displayBackRoute = true;

    constructor(
        private reportingService: PayrollReportingService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private analytics: AnalyticService,
        private auth: AuthService,
        private notify: NotifyService,
        private changeDetector: ChangeDetectorRef
    ) {
        if (
            this.activatedRoute?.snapshot?.queryParams.payrollType &&
            this.activatedRoute?.snapshot?.queryParams.payPeriod
        ) {
            this.urlPassedFilters = this.activatedRoute?.snapshot?.queryParams;
            this.displayBackRoute = false;
        }
    }

    async ngOnInit(): Promise<void> {
        this.report = this.getReport(this.activatedRoute.snapshot.url);

        if (!this.report) {
            this.router.navigateByUrl('/404');
            return;
        }

        this.hasMultipleExportOptions =
            [this.report.hasPDF, this.report.hasExcel, this.report.hasCSV].filter((x) => x).length > 1;

        this.loadSchedule(this.report)
            .then(() => {
                if (this.urlPassedFilters) {
                    this.filterByUrlQueryParams(this.schedule, this.urlPassedFilters);
                } else {
                    this.display(this.report);
                }
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'reporting.payroll.couldNotLoadReport');
            });
    }

    onFilter(dates: any, includePending: boolean, form: any): void {
        if (dates.startAt == this.startAt && dates.endAt == this.endAt && includePending == this.includePending) {
            return;
        }
        this.setDates(dates.startAt, dates.endAt);
        this.includePending = includePending;
        if (this.report) {
            this.display(this.report).then(() => this.trackEvent(this.getFilterAnalyticsEvent()));
        }
    }

    exportExcel(): void {
        if (!this.report) {
            return;
        }
        this.reportingService.exportExcel(this.report, this.getReportOptions(this.report));

        this.trackEvent(this.getExcelExportAnalyticsEvent());
    }

    exportPDF(): void {
        if (!this.report) {
            return;
        }
        this.reportingService.exportPDF(this.report, this.getReportOptions(this.report));

        this.trackEvent(this.getPDFExportAnalyticsEvent());
    }

    exportCSV(): void {
        if (!this.report) {
            return;
        }
        this.reportingService.exportCSV(this.report, this.getReportOptions(this.report));

        this.trackEvent(this.getCSVExportAnalyticsEvent());
    }

    setActiveSheet(index = 0): void {
        if (!this.reportBook) {
            return;
        }
        const sheets = this.reportBook.sheets;

        if (!sheets || !sheets.length) {
            return;
        }

        this.activeSheet = sheets[index];
        this.activeSheetIndex = index;
        this.setNumberOfStickyColumns(this.activeSheet);
        this.sticky();
    }

    /**
     * This formatting should be done on the back end
     * Each report version should define it's own formatting
     * This is a workaround.
     */
    shouldFormat(index: number): boolean {
        if (!this.reportBook) {
            return false;
        }
        return !this.reportBook.columnsNotToFormat[this.activeSheetIndex].includes(index);
    }

    trackByFn(index: number): number {
        return index;
    }

    protected sticky(): void {
        setTimeout(() => {
            stickybits('.left-sticky');
            stickybits('.top-sticky');
        });
    }

    private getDefaultYear(): number {
        const currentYear = moment().year();

        if (!this.schedule) {
            return currentYear;
        }

        const lastPendingPeriod = this.schedule.lastPendingPeriod;
        if (lastPendingPeriod?.chequeDate) {
            return moment(lastPendingPeriod.chequeDate).year();
        }

        const lastPaidPeriod = this.schedule.lastPaidPeriod;
        if (lastPaidPeriod?.chequeDate) {
            return moment(lastPaidPeriod.chequeDate).year();
        }

        return currentYear;
    }

    private filterByUrlQueryParams(schedule: Schedule, urlPassedFilters: FilterValues): void {
        const defaultYear = urlPassedFilters?.year ? urlPassedFilters.year : this.getDefaultYear();
        const year = schedule['allPeriods'].find((period) => period.year == defaultYear);
        year.payPeriods = year.payPeriods.filter(
            ({ payrollState, periodNo }) => payrollState && payrollState !== 'open' && periodNo !== 0
        );
        let period: PayPeriod;
        if (urlPassedFilters?.payrollType === 'off-cycle') {
            [period] = year.payPeriods.filter(
                (payPeriod: PayPeriod) =>
                    payPeriod.type === 'OffCycle' && payPeriod.chequeDate == urlPassedFilters.payPeriod
            );
        } else {
            [period] = year.payPeriods.filter(
                (payPeriod: PayPeriod) => payPeriod.periodNo == urlPassedFilters.payPeriod
            );
        }
        const chequeDate = moment(period.chequeDate).startOf('day').format('YYYY-MM-DD');
        const dates = {
            startAt: chequeDate,
            endAt: chequeDate,
        };
        this.urlPassedFilters = null;
        this.onFilter(dates, this.filterForm.includePending, this.filterForm);
    }

    private getPayPeriodDateRange(): void {
        if (!this.schedule) {
            return;
        }
        const selectedPayPeriods = this.schedule['allPeriods']
            .flatMap((yearItem) => {
                return yearItem.year >= parse(this.startAt).getFullYear() &&
                    yearItem.year <= parse(this.endAt).getFullYear()
                    ? yearItem.payPeriods
                    : [];
            })
            .filter((period: PayPeriod) => isWithinRange(parse(period.chequeDate), this.startAt, this.endAt));

        const firstPayPeriod = selectedPayPeriods.slice().at(0);
        const lastPayPeriod = selectedPayPeriods.slice().at(-1);

        this.payPeriodsStartAt = firstPayPeriod.startDate;
        this.payPeriodsEndAt = lastPayPeriod.endDate;
        this.firstChequeDate = firstPayPeriod.chequeDate;
        this.lastChequeDate = lastPayPeriod.chequeDate;
    }

    private display(report: PayrollReport): Promise<void> {
        this.isLoading = true;

        return this.reportingService
            .display(report, this.getReportOptions(report))
            .then((jsonApiResponse: any) => {
                this.reportBook = new ReportBook(jsonApiResponse.data.attributes).formatForDisplay();
                this.setActiveSheet();
                this.isLoading = false;
                this.changeDetector.detectChanges();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'reporting.payroll.loadedWithErrors');
            });
    }

    /**
     * Find the report that matches the final url segment
     * We do not use the `report` param because it is possible
     * to be linked to this report and not have the `report` param set.
     */
    private getReport(url: UrlSegment[]): PayrollReport | undefined {
        const reportUrlLastSegment = url[url.length - 1];
        const reports = Reports.map((report) => new report());
        return reports.find(({ routerLink }) => routerLink === reportUrlLastSegment.path);
    }

    private async loadSchedule(report: PayrollReport): Promise<void> {
        try {
            const res = await this.http.get(await this.getSchedulePath()).toPromise();
            this.schedule = new Schedule(res['data']['attributes']);
            const [startAt, endAt] = report.getInitialDates(this.schedule);
            this.setDates(startAt, endAt);
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private getReportOptions(report: PayrollReport): ReportOptions {
        const options: ReportOptions = {
            from: format(this.startAt, 'YYYY-MM-DD'),
            to: format(this.endAt, 'YYYY-MM-DD'),
        };

        if (report.allowsInclusionOfPendingPayrolls) {
            options.include_pending_payrolls = this.includePending;
        }

        if (report instanceof PayrollRegister) {
            options.separate_compensations = true;
        }

        return options;
    }

    private setNumberOfStickyColumns(activeSheet: ReportSheet): void {
        this.numberOfStickyColumns = activeSheet['cardinalColumns'].length;
    }

    private setDates(startAt: Date, endAt: Date): void {
        this.isSingleDayRange = moment(startAt).isSame(endAt, 'day');

        this.startAt = startAt;
        this.endAt = endAt;

        this.getPayPeriodDateRange();
    }

    private trackEvent(event?: AnalyticEvents): void {
        if (!event) {
            return;
        }

        this.analytics.trackEvent(event);
    }

    private getFilterAnalyticsEvent(): AnalyticEvents | undefined {
        if (this.report instanceof PayrollRegister) {
            return AnalyticEvents.ReportingPayrollPayrollRegisterSaveFilter;
        } else if (this.report instanceof YTD) {
            return AnalyticEvents.ReportingPayrollYTDReportSaveFilter;
        } else if (this.report instanceof Remittance) {
            return AnalyticEvents.ReportingPayrollRemittanceReportSaveFilter;
        } else if (this.report instanceof JournalEntry) {
            return AnalyticEvents.ReportingPayrollJournalEntryReportSaveFilter;
        } else if (this.report instanceof Benefit) {
            return AnalyticEvents.ReportingPayrollBenefitsReportSaveFilter;
        }
    }

    private getExcelExportAnalyticsEvent(): AnalyticEvents | undefined {
        if (this.report instanceof PayrollRegister) {
            return AnalyticEvents.ReportingPayrollPayrollRegisterExportExcel;
        } else if (this.report instanceof YTD) {
            return AnalyticEvents.ReportingPayrollYTDReportExportExcel;
        } else if (this.report instanceof Remittance) {
            return AnalyticEvents.ReportingPayrollRemittanceReportExportExcel;
        } else if (this.report instanceof JournalEntry) {
            return AnalyticEvents.ReportingPayrollJournalEntryReportExportExcel;
        } else if (this.report instanceof Benefit) {
            return AnalyticEvents.ReportingPayrollBenefitsReportExportExcel;
        }
    }

    private getPDFExportAnalyticsEvent(): AnalyticEvents | undefined {
        if (this.report instanceof PayrollRegister) {
            return AnalyticEvents.ReportingPayrollPayrollRegisterExportPDF;
        } else if (this.report instanceof JournalEntry) {
            return AnalyticEvents.ReportingPayrollJournalEntryReportExportPDF;
        }
    }

    private getCSVExportAnalyticsEvent(): AnalyticEvents | undefined {
        if (this.report instanceof JournalEntry) {
            return AnalyticEvents.ReportingPayrollJournalEntryReportExportCSV;
        } else if (this.report instanceof Benefit) {
            return AnalyticEvents.ReportingPayrollBenefitsReportExportCSV;
        }
    }

    private getSchedulePath(): string {
        return PayrollResources.Schedule.replace(':company', this.auth.company.id.toString());
    }
}
